import { useMemo, useState } from "react";
import { Icon, IconButton, InputAdornment, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { formatarNumero, formatarPercentualCurto, rotuloCurtoMes, temaGrafico } from "./analiseUtils";
import { formatarMoedaCentavos } from "./acordosUtils";

const ORDENACOES = [
    { id: "atendimentos", value: "atendimentos", label: "Atendimentos" },
    { id: "fichas", value: "fichas", label: "Fichas acionadas" },
    { id: "acordos", value: "acordos", label: "Negociações" },
    { id: "conversao", value: "conversao", label: "Conversão" },
    { id: "valorAcordos", value: "valorAcordos", label: "Valor negociado" },
    { id: "recebido", value: "recebido", label: "Valor recebido" },
    { id: "ticketMedio", value: "ticketMedio", label: "Ticket médio" },
    { id: "nome", value: "nome", label: "Nome" },
];

const COLUNAS = [
    { id: "fichas", rotulo: "Fichas Acionadas", formato: "numero", ajuda: "Fichas distintas acionadas no período." },
    { id: "atendimentos", rotulo: "Atendimentos", formato: "numero", ajuda: "Registros de histórico (categoria Ocorrência) no período." },
    { id: "acordos", rotulo: "Negociações", formato: "numero", ajuda: "Acordos formalizados no período." },
    { id: "conversao", rotulo: "Conversão", formato: "pct", ajuda: "Fichas acionadas que tiveram acordo de quem as acionou ÷ fichas acionadas." },
    { id: "valorAcordos", rotulo: "Valor Negociado", formato: "moeda", ajuda: "Soma do valor dos acordos formalizados no período." },
    { id: "recebido", rotulo: "Valor Recebido", formato: "moeda", ajuda: "Pagamentos no período: parcelas de acordo e baixa avulsa." },
    { id: "ticketMedio", rotulo: "Ticket Médio", formato: "moeda", ajuda: "Valor negociado ÷ negociações." },
];

const formatar = (formato, valor) => {
    if (valor === null || valor === undefined) return "–";
    if (formato === "moeda") return formatarMoedaCentavos(valor);
    if (formato === "pct") return formatarPercentualCurto(valor);
    return formatarNumero(valor);
};

/** Mini gráfico de barras dos atendimentos mês a mês (forma da evolução de cada linha; o número exato fica no título). */
function Evolucao({ valores, meses, cor, vazio }) {
    const maximo = Math.max(...valores, 0);
    const largura = 10;
    const espaco = 3;
    const altura = 18;
    const titulo = valores.map((v, i) => `${rotuloCurtoMes(meses[i])}: ${formatarNumero(v)}`).join(" · ");
    return (
        <Tooltip title={`Atendimentos — ${titulo}`} placement="top">
            <svg width={valores.length * (largura + espaco)} height={altura} role="img" aria-label={`Atendimentos por mês: ${titulo}`}>
                {valores.map((v, i) => {
                    const h = maximo > 0 ? Math.max((v / maximo) * altura, v > 0 ? 2 : 1) : 1;
                    return (
                        <rect
                            key={meses[i]}
                            x={i * (largura + espaco)}
                            y={altura - h}
                            width={largura}
                            height={h}
                            rx={1.5}
                            fill={v > 0 ? cor : vazio}
                        />
                    );
                })}
            </svg>
        </Tooltip>
    );
}

/**
 * Desempenho de cada negociador (ou coordenador) no período: atendimento e acordos lado a lado, com o total.
 * O olho põe a pessoa no filtro da tela (junta com as já escolhidas); clicar de novo tira.
 */
export default function TabelaDesempenho({ titulo, linhas, total, meses, cores, selecionados = [], onSelecionar, rotuloPessoa }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const tema = temaGrafico(darkMode);
    const [busca, setBusca] = useState("");
    const [ordem, setOrdem] = useState(ORDENACOES[0]);

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const campo = ordem.value;
        return linhas
            .filter(l => !termo || l.nome.toLowerCase().includes(termo))
            .sort((a, b) => {
                if (campo === "nome") return a.nome.localeCompare(b.nome);
                return (b[campo] ?? -Infinity) - (a[campo] ?? -Infinity) || a.nome.localeCompare(b.nome);
            });
    }, [linhas, busca, ordem]);

    const celula = {
        display: "table-cell",
        py: 0.75,
        px: 1.25,
        fontSize: "0.8rem",
        color: `${tema.texto} !important`,
        borderBottom: `1px solid ${tema.borda}`,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
    };
    const cabecalho = { ...celula, fontWeight: "bold", color: `${tema.textoSecundario} !important`, fontSize: "0.75rem", verticalAlign: "bottom" };
    const fundoSelecionado = darkMode ? "rgba(255, 255, 255, 0.10)" : "rgba(26, 115, 232, 0.08)";
    const fundoTotal = darkMode ? "rgba(255, 255, 255, 0.06)" : "#f0f2f5";

    const renderLinha = (linha, posicao) => {
        const ehTotal = posicao === null;
        const ativo = !ehTotal && selecionados.includes(linha.nome);
        let dica = `Ver só ${linha.nome} na tela`;
        if (ativo) dica = `Tirar ${linha.nome} do filtro`;
        else if (selecionados.length) dica = `Incluir ${linha.nome} no filtro`;
        return (
            <TableRow
                key={ehTotal ? "__total__" : linha.nome}
                sx={{ display: "table-row", background: ehTotal ? fundoTotal : ativo ? fundoSelecionado : "transparent" }}
            >
                <TableCell sx={{ ...celula, color: `${tema.textoSecundario} !important`, width: 32 }}>{ehTotal ? "" : posicao}</TableCell>
                <TableCell sx={{ ...celula, fontWeight: "bold" }}>
                    <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                        {!ehTotal && (
                            <MDBox component="span" sx={{ width: 10, height: 10, minWidth: 10, borderRadius: "2px", background: cores.cor(linha.nome) }} />
                        )}
                        {linha.nome}
                    </MDBox>
                </TableCell>
                {COLUNAS.map(col => (
                    <TableCell key={col.id} align="right" sx={{ ...celula, fontWeight: ehTotal ? "bold" : undefined }}>
                        {formatar(col.formato, linha[col.id])}
                    </TableCell>
                ))}
                <TableCell align="center" sx={celula}>
                    <Evolucao
                        valores={linha.atendimentosMes}
                        meses={meses}
                        cor={ehTotal ? tema.texto : cores.cor(linha.nome)}
                        vazio={tema.grade}
                    />
                </TableCell>
                <TableCell align="center" sx={{ ...celula, py: 0 }}>
                    {!ehTotal && (
                        <Tooltip title={dica} placement="left">
                            <IconButton size="small" color="info" onClick={() => onSelecionar(linha.nome)} aria-label={dica}>
                                <Icon fontSize="small">{ativo ? "visibility_off" : "visibility"}</Icon>
                            </IconButton>
                        </Tooltip>
                    )}
                </TableCell>
            </TableRow>
        );
    };

    return (
        <MDBox>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1.5} flexWrap="wrap" mb={1.5}>
                <MDTypography variant="button" fontWeight="bold" color="text">{titulo}</MDTypography>
                <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <MDBox sx={{ width: 240 }}>
                    <MDInput
                        size="small"
                        fullWidth
                        placeholder={`Buscar ${rotuloPessoa.toLowerCase()}...`}
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon fontSize="small" sx={{ color: `${tema.textoSecundario} !important` }}>search</Icon>
                                </InputAdornment>
                            ),
                        }}
                    />
                </MDBox>
                <MDBox display="flex" alignItems="center" gap={1} sx={{ color: "inherit" }}>
                    <MDBox component="span" sx={{ fontSize: "0.75rem", fontWeight: "bold", color: tema.textoSecundario, whiteSpace: "nowrap" }}>
                        Ordenar por
                    </MDBox>
                    <MDBox sx={{ width: 180 }}>
                        <MDSelect isMulti={false} name="ordenarDesempenho" value={ordem} options={ORDENACOES} onChange={v => setOrdem(v || ORDENACOES[0])} />
                    </MDBox>
                </MDBox>
                </MDBox>
            </MDBox>
            <TableContainer sx={{ boxShadow: "none", background: "transparent", borderRadius: 0 }}>
                <Table size="small" sx={{ display: "table", minWidth: 1100 }}>
                    <TableHead sx={{ display: "table-header-group" }}>
                        <TableRow sx={{ display: "table-row" }}>
                            <TableCell sx={cabecalho}>#</TableCell>
                            <TableCell sx={cabecalho}>{rotuloPessoa}</TableCell>
                            {COLUNAS.map(col => (
                                <TableCell key={col.id} align="right" sx={cabecalho}>
                                    <Tooltip title={col.ajuda} placement="top"><span>{col.rotulo}</span></Tooltip>
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={cabecalho}>
                                <Tooltip title="Atendimentos mês a mês" placement="top"><span>Evolução Mensal</span></Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={cabecalho}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ display: "table-row-group" }}>
                        {visiveis.map((l, i) => renderLinha(l, i + 1))}
                        {visiveis.length === 0 && (
                            <TableRow sx={{ display: "table-row" }}>
                                <TableCell colSpan={COLUNAS.length + 4} align="center" sx={{ ...celula, color: `${tema.textoSecundario} !important` }}>
                                    Nenhum {rotuloPessoa.toLowerCase()} com &quot;{busca}&quot;.
                                </TableCell>
                            </TableRow>
                        )}
                        {renderLinha(total, null)}
                    </TableBody>
                </Table>
            </TableContainer>
        </MDBox>
    );
}
